import { Node, mergeAttributes } from '@tiptap/core';

/**
 * TestimonyGenerator Extension
 * Creates a button that uses AI to combine three testimony text areas into a cohesive story
 */
export const TestimonyGenerator = Node.create({
  name: 'testimonyGenerator',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-id'),
        renderHTML: (attributes) => ({
          'data-id': attributes.id,
        }),
      },
      description: {
        default:
          'After filling in the three testimony sections above, click the button below to have AI combine them into a cohesive story you can share with others.',
        parseHTML: (element) => element.getAttribute('data-description'),
        renderHTML: (attributes) => ({
          'data-description': attributes.description,
        }),
      },
      // Comma-separated questionIds ('beforeId,momentId,afterId') to pull from the
      // user's saved lesson_responses server-side — lets the three drafting text
      // areas live in an earlier session. Empty = legacy same-page DOM lookup.
      sourceIds: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-source-ids') || '',
        renderHTML: (attributes) => ({ 'data-source-ids': attributes.sourceIds || '' }),
      },
      // Prompt variant on /research/testimony (e.g. 'baptism' = 60-second story,
      // Lordship framing, "leave out" guardrails). Empty = default prompt.
      variant: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-variant') || '',
        renderHTML: (attributes) => ({ 'data-variant': attributes.variant || '' }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="testimony-generator"]',
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const id = node.attrs.id || HTMLAttributes['data-id'] || `testimony-gen-${Date.now()}`;
    const description =
      node.attrs.description ||
      HTMLAttributes['data-description'] ||
      'After filling in the three testimony sections above, click the button below to have AI combine them into a cohesive story you can share with others.';

    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'testimony-generator',
        'data-id': id,
        'data-description': description,
        class: 'testimony-generator-block',
        style: 'margin: 2rem 0;',
      }),
      [
        'div',
        {
          class: 'testimony-generator-inner',
          style:
            'background: #fef3c7; border: 2px solid #f59e0b; border-radius: 12px; padding: 1.5rem;',
        },
        [
          'div',
          {
            class: 'testimony-header',
            style: 'display: flex; align-items: center; gap: 0.75rem; margin-bottom: 1rem;',
          },
          [
            'span',
            { style: 'font-size: 1.5rem;' },
            '\u2728', // Sparkle emoji
          ],
          [
            'span',
            {
              class: 'testimony-title',
              style: 'font-weight: 700; color: #92400e; font-size: 1.1rem;',
            },
            'Generate My Testimony Story',
          ],
        ],
        [
          'p',
          {
            class: 'testimony-description',
            style: 'color: #92400e; font-size: 0.95rem; line-height: 1.6; margin: 0 0 1rem 0;',
          },
          description,
        ],
        [
          'button',
          {
            type: 'button',
            class: 'generate-testimony-btn',
            style:
              'background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); color: white; border: none; padding: 0.875rem 1.5rem; border-radius: 8px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 0.5rem; font-size: 1rem;',
          },
          '\u2728 Generate My Testimony Story',
        ],
        [
          'div',
          {
            class: 'testimony-loading',
            style: 'display: none; margin-top: 1rem; color: #92400e;',
          },
          '\u23F3 Generating your testimony story...',
        ],
        [
          'div',
          {
            class: 'testimony-output',
            style: 'display: none; margin-top: 1.5rem;',
          },
          [
            'label',
            {
              style: 'display: block; font-weight: 600; color: #92400e; margin-bottom: 0.75rem;',
            },
            'Your Generated Testimony:',
          ],
          [
            'textarea',
            {
              class: 'generated-testimony-text',
              rows: '12',
              style:
                'width: 100%; border: 2px solid #f59e0b; border-radius: 8px; padding: 1.25rem; font-size: 1.05rem; font-family: inherit; resize: vertical; background: white; color: #1f2937; line-height: 1.7;',
            },
          ],
          [
            'div',
            { style: 'display: flex; flex-wrap: wrap; gap: 0.75rem; margin-top: 1rem;' },
            [
              'button',
              {
                type: 'button',
                class: 'copy-testimony-btn',
                style:
                  'background: #3b82f6; color: white; border: none; padding: 0.75rem 1.25rem; border-radius: 6px; font-weight: 600; cursor: pointer;',
              },
              '\uD83D\uDCCB Copy',
            ],
            [
              'button',
              {
                type: 'button',
                class: 'save-testimony-btn',
                style:
                  'background: #059669; color: white; border: none; padding: 0.75rem 1.25rem; border-radius: 6px; font-weight: 600; cursor: pointer;',
              },
              'Save My Testimony',
            ],
            [
              'button',
              {
                type: 'button',
                class: 'regenerate-testimony-btn',
                style:
                  'background: #6b7280; color: white; border: none; padding: 0.75rem 1.25rem; border-radius: 6px; font-weight: 600; cursor: pointer;',
              },
              'Regenerate',
            ],
          ],
          [
            'div',
            {
              class: 'testimony-save-status',
              style: 'margin-top: 0.75rem; font-size: 0.9rem;',
            },
          ],
        ],
      ],
    ];
  },

  addCommands() {
    return {
      setTestimonyGenerator:
        (attributes) =>
        ({ commands }) => {
          const id = `testimony-gen-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          return commands.insertContent({
            type: this.name,
            attrs: {
              id,
              ...attributes,
            },
          });
        },
    };
  },
});

export default TestimonyGenerator;
